import { useInfiniteQuery } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";

import { triggersV2QueryKeys } from "./triggersV2QueryKeys";

const DEFAULT_PAGE_SIZE = 25;

/** Keyset-paged dispatches for one trigger, newest first. */
export function useTriggerDispatches(
  triggerId: string | undefined,
  options?: { pageSize?: number; orgId?: string },
) {
  const { triggerV2Service } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(options?.orgId);

  return useInfiniteQuery({
    queryKey: triggersV2QueryKeys.dispatches(resolvedOrgId, triggerId ?? ""),
    queryFn: ({ pageParam, signal }) => {
      if (triggerId === undefined) {
        return Promise.reject(new Error("Invalid trigger id"));
      }
      return triggerV2Service.getTriggerDispatches(triggerId, {
        abortSignal: signal,
        limit: options?.pageSize ?? DEFAULT_PAGE_SIZE,
        pageToken: pageParam || undefined,
      });
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.next_token,
    enabled: triggerId !== undefined,
    staleTime: 15_000,
  });
}

/** Keyset-paged dispatches whose subject is one dataset, newest first. */
export function useDatasetTriggerDispatches(
  datasetId: string | undefined,
  options?: { pageSize?: number; orgId?: string },
) {
  const { triggerV2Service } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(options?.orgId);

  return useInfiniteQuery({
    queryKey: triggersV2QueryKeys.datasetDispatches(
      resolvedOrgId,
      datasetId ?? "",
    ),
    queryFn: ({ pageParam, signal }) => {
      if (datasetId === undefined) {
        return Promise.reject(new Error("Invalid dataset id"));
      }
      return triggerV2Service.getDatasetDispatches(datasetId, resolvedOrgId, {
        abortSignal: signal,
        limit: options?.pageSize ?? DEFAULT_PAGE_SIZE,
        pageToken: pageParam || undefined,
      });
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.next_token,
    enabled: datasetId !== undefined,
    staleTime: 15_000,
  });
}
